var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var _a;
import { IdMap, RArray, RMap, cartesianProduct, toPairs } from "../collections";
import { makeImmutable } from "../core";
import { CustomParameterSet, Menu, MenuSection, MenuSectionId, MenuSectionItemId, ProductInstanceVariant, } from "../ordering";
import { perRestaurant } from "../di";
let MenuSectionParser = class MenuSectionParser {
    constructor(menu) {
        this.productTypes = menu.productTypes;
        this.customParameterTypes = menu.customParameterTypes;
        makeImmutable(this);
    }
    parseSectionParameterChoices(customParameterValues) {
        return RMap.fromIterable(toPairs(customParameterValues).mapOptional(([customParameterTypeId, customParameterChoiceId]) => {
            const customParameterType = this.customParameterTypes.rawFind(customParameterTypeId);
            if (customParameterType === null) {
                return null;
            }
            const customParameterChoice = customParameterType.choices.rawFind(customParameterChoiceId);
            if (customParameterChoice === null ||
                customParameterChoice.availability === "TemporarilyUnavailable") {
                return null;
            }
            return [customParameterType, customParameterChoice.id];
        }));
    }
    parse(activeSections) {
        return IdMap.fromIterable(activeSections.mapOptional(([sectionId, section]) => this.parseSection(sectionId, section)));
    }
    parseSection(id, section) {
        if (section.content.tag !== "Products") {
            return null;
        }
        const sectionParameterChoices = this.parseSectionParameterChoices(section.customParameterValues);
        const contentItems = new RArray(section.content.items);
        const productItems = contentItems.mapOptional(([, item]) => item.tag === "SingleProduct"
            ? this.productTypes.rawFind(item.itemProduct)
            : null);
        const creatorItems = RMap.fromIterable(contentItems.mapOptional(([sectionItemId, item]) => {
            if (item.tag !== "ProductCreator") {
                return null;
            }
            const creatorProducts = new RArray(item.creatorProducts).mapOptional((creatorProduct) => this.productTypes.rawFind(creatorProduct));
            if (creatorProducts.isEmpty) {
                return null;
            }
            return [new MenuSectionItemId(sectionItemId), creatorProducts];
        }));
        return new MenuSection({
            id: new MenuSectionId(id),
            variants: this.parseVariants(section, sectionParameterChoices, productItems),
            columns: this.parseColumns(section, sectionParameterChoices, productItems),
            creators: this.parseCreators(sectionParameterChoices, creatorItems),
        });
    }
    parseVariants(section, sectionParameterChoices, productItems) {
        const variantsParameterChoices = this.parseVariantCustomParameters(sectionParameterChoices, section.dropdownParameters);
        return RMap.fromIterable(productItems.map((productType) => {
            return [
                productType.id,
                variantsParameterChoices.map((variantParameterChoices) => new ProductInstanceVariant({
                    productTypes: { type: "Single", productType },
                    customParameterChoices: CustomParameterSet.fromChoices(variantParameterChoices.union(sectionParameterChoices)),
                })),
            ];
        }));
    }
    parseColumns(section, sectionParameterChoices, productItems) {
        if (section.columnParameter === null) {
            return RMap.empty();
        }
        const columnParameter = this.customParameterTypes.rawFind(section.columnParameter);
        if (columnParameter === null) {
            return RMap.empty();
        }
        return RMap.fromIterable(productItems.map((productType) => [
            productType.id,
            this.parseColumn(columnParameter).map((columnParameterChoices) => new ProductInstanceVariant({
                productTypes: { type: "Single", productType },
                customParameterChoices: CustomParameterSet.fromChoices(columnParameterChoices.union(sectionParameterChoices)),
            })),
        ]));
    }
    parseVariantCustomParameters(sectionParameterChoices, dropdownParameterIds) {
        const dropdownCustomParameters = new RArray(dropdownParameterIds).mapOptional((dropdownParameterId) => {
            const dropdownParameter = this.customParameterTypes.rawGet(dropdownParameterId);
            if (sectionParameterChoices.includes(dropdownParameter)) {
                // NOTICE Exclude parameter that has fixed choice inside of the section
                return null;
            }
            return dropdownParameter;
        });
        const dropdownParametersPair = dropdownCustomParameters.map((parameter) => [parameter, new RArray(parameter.choiceIds)]);
        const parameterCombinations = cartesianProduct(dropdownParametersPair);
        return parameterCombinations.map((combination) => RMap.fromIterable(combination.entries()));
    }
    parseColumn(columnParameter) {
        return RMap.fromIterable(columnParameter.choiceIds
            .rawValues()
            .map((customParameterChoiceId) => [
            customParameterChoiceId,
            RMap.singleton(columnParameter, customParameterChoiceId),
        ]));
    }
    parseCreators(sectionParameterChoices, creatorItems) {
        const customParameterChoices = CustomParameterSet.fromChoices(sectionParameterChoices);
        return creatorItems.map((creatorProductTypes) => new ProductInstanceVariant({
            productTypes: creatorProductTypes.size === 1
                ? { type: "Single", productType: creatorProductTypes.get(0) }
                : {
                    type: "Halves",
                    first: creatorProductTypes.get(0),
                    second: creatorProductTypes.get(1),
                },
            customParameterChoices,
        }));
    }
};
MenuSectionParser = __decorate([
    perRestaurant(),
    __metadata("design:paramtypes", [typeof (_a = typeof Menu !== "undefined" && Menu) === "function" ? _a : Object])
], MenuSectionParser);
export { MenuSectionParser };
